import { useEffect, useRef, useState, type FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';

/**
 * Phase 12.3G — lifecycle confirmation, per `COMPANIES_DESIGN.md` v1.0 §6.3.
 *
 * Friction tracks reversibility, which is the whole design of this dialog:
 *
 * | Action     | Reason     | Type-to-confirm |
 * |------------|------------|-----------------|
 * | Activate   | optional   | no              |
 * | Suspend    | MANDATORY  | no              |
 * | Reactivate | optional   | no              |
 * | Cancel     | MANDATORY  | YES — the name  |
 *
 * Suspend is reversible but takes a company's users offline immediately, so it
 * earns a mandatory reason for the audit trail. Cancel is terminal (D-5), so it
 * earns the name typed out in full. Activate is benign and gets a plain
 * confirm — ceremony where nothing is at stake only teaches people to click
 * through ceremony where something is.
 */

export type LifecycleAction = 'activate' | 'suspend' | 'reactivate' | 'cancel';

interface ActionCopy {
  title: string;
  /** Stated plainly, including the consequence where there is one. */
  body: string;
  confirmLabel: string;
  reasonRequired: boolean;
  typeToConfirm: boolean;
  destructive: boolean;
}

export const LIFECYCLE_COPY: Record<LifecycleAction, ActionCopy> = {
  activate: {
    title: 'Activate company',
    body: 'This moves the company from Trial to Active. Its users already have access during Trial, so nothing changes for them today — this records the commercial decision.',
    confirmLabel: 'Activate',
    reasonRequired: false,
    typeToConfirm: false,
    destructive: false,
  },
  suspend: {
    title: 'Suspend company',
    body: 'The company’s users will lose access immediately. No data is deleted, and the company can be reactivated at any time.',
    confirmLabel: 'Suspend',
    reasonRequired: true,
    typeToConfirm: false,
    destructive: true,
  },
  reactivate: {
    title: 'Reactivate company',
    body: 'This restores access for the company’s users. Nothing is recreated — their data was retained throughout the suspension.',
    confirmLabel: 'Reactivate',
    reasonRequired: false,
    typeToConfirm: false,
    destructive: false,
  },
  cancel: {
    title: 'Cancel company',
    body: 'Cancellation is permanent and cannot be undone. The company’s users lose access and the company cannot be reactivated. All data is RETAINED — this is retirement, not deletion.',
    confirmLabel: 'Cancel company',
    reasonRequired: true,
    typeToConfirm: true,
    destructive: true,
  },
};

interface Props {
  action: LifecycleAction | null;
  companyName: string;
  submitting: boolean;
  error: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export function CompanyLifecycleDialog({
  action,
  companyName,
  submitting,
  error,
  onClose,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // Every open starts clean — a reason typed for a suspend must never be
  // carried into a cancel.
  useEffect(() => {
    if (action) {
      setReason('');
      setTyped('');
      // Focus the first meaningful control rather than the dialog container.
      const timer = setTimeout(() => reasonRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [action]);

  if (!action) return null;

  const copy = LIFECYCLE_COPY[action];
  const reasonOk = !copy.reasonRequired || reason.trim().length > 0;
  const nameOk = !copy.typeToConfirm || typed.trim() === companyName.trim();
  const canSubmit = reasonOk && nameOk && !submitting;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm(reason.trim());
  };

  return (
    <Modal open onClose={submitting ? () => undefined : onClose} title={copy.title} size="md">
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <p className="text-sm text-muted-foreground">{copy.body}</p>

        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
          <p className="text-xs text-muted-foreground">Company</p>
          <p className="break-words text-sm font-medium text-foreground">{companyName}</p>
        </div>

        <div>
          <label
            htmlFor="lifecycle-reason"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Reason{' '}
            {copy.reasonRequired ? (
              <span className="text-destructive">*</span>
            ) : (
              <span className="text-muted-foreground">(optional)</span>
            )}
          </label>
          <textarea
            id="lifecycle-reason"
            ref={reasonRef}
            rows={3}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            required={copy.reasonRequired}
            placeholder={
              copy.reasonRequired
                ? 'Recorded in the platform audit trail — say why.'
                : 'Optional note for the audit trail.'
            }
            className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {copy.typeToConfirm && (
          <div>
            <label
              htmlFor="lifecycle-confirm-name"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              Type <span className="font-semibold">{companyName}</span> to confirm
            </label>
            <input
              id="lifecycle-confirm-name"
              type="text"
              value={typed}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
              className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
            />
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive"
            />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Full-width, stacked below sm — Modal Standards. */}
        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
          >
            Keep as is
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={
              copy.destructive
                ? 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0'
                : 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0'
            }
          >
            {submitting && (
              <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
            )}
            {copy.confirmLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
